import { IChildProp } from '@intelligence/components/Form/type';
import {
  TDataSource,
  TDataSourceMetrics,
  convertDataSourceAndSource,
} from '@intelligence/_config/enumeration/dataSource';

const PC_AND_CONSOLE_DATA_SOURCE: Record<TDataSource, TDataSourceMetrics[]> = {
  mscience: ['digitalRevenue', 'units', 'digitalARPPU', 'ast', 'mscienceLifetimeDigitalRevenue', 'mscienceLifetimeDigitalUnits'],
  databrain: [
    'databrain__total_revenue',
    'databrain__lifetime_total_revenue',
    'databrain__base_game_sales_units',
    'databrain__lifetime_base_game_sales_units',
  ],
  steamData: [
    'steamData_SteamPCU',
    'steamData_SteamACU',
    'steamData_SteamFollowers',
    'steamData_SteamReviews',
    'steamData_premiumRevenue',
    'steamData_cumulativeRevenue',
    'steamData_unit',
    'steamData_cumulativeUnits',
    'steamData_price',
    'steamData_lifetimeWeightedPrice',
    'steamData_playersByCountry',
    'steamData_avgPlaytime',
    'steamData_totalFollowers',
  ],
  gsd: ['digitalUnitsGSD', 'physicalUnitsGSD', 'digitalRevenueGSD', 'physicalRevenueGSD'],
  npd: ['physicalRevenue', 'physicalUnits'],
  ampere: [
    'ampere_mau',
    'ampere_previousMAU',
    'ampere_addedMAU',
    'ampere_newMAU',
    'ampere_retainedMAU',
    'ampere_churnedMAU',
    'ampere_dau',
    'ampere_newDAU',
    'ampere_avgMonthlyPlaytimeAmpere',
    'ampere_acquisitionRate',
    'ampere_stickness',
    'ampere_lifetimePlayers',
    'ampere_avgDaillyPlaytime',
    'ampere_d1',
    'ampere_d7',
    'ampere_d14',
    'ampere_d28',
    'ampere_d60',
    'ampere_rollingD1',
    'ampere_rollingD7',
    'ampere_rollingD14',
    'ampere_rollingD28',
    'ampere_rollingD60',
  ],
  hatchet: ['hoursWatched', 'airtime', 'peakCCV', 'avgCCV'],
  // Famitsu is temporarily unavailable in PC&Console detail downloads.
  // famitsu: [
  //   'famitsu_salesOfSelectedTime',
  //   'famitsu_revenueOfSelectedTime',
  //   'famitsu_cumulativeSales',
  //   'famitsu_cumulativeRevenueFamitsu',
  //   'famitsu_priceFamitsu',
  // ],
  noUpdate: [
    'mau',
    'playerShare',
    'mauGrowth',
    'mauNew',
    'mauReturning',
    'lifetimePlayer',
    'timeSpent0h',
    'timeSpent1h',
    'timeSpent5h',
    'timeSpent10h',
    'timeSpent25h',
    'timeSpent50h',
    'avgDailyPlaytime',
    'avgDailySessions',
    'avgPlaytimePerSession',
    'avgMonthlyPlaytime',
    'avgMonthlySessions',
    'avgDaysPlayed',
    'stickness',
    'totalHours',
    'churn',
    'acquisition',
    'retentionD1',
    'retentionD2',
    'retentionD4',
    'retentionD7',
    'retentionD14',
    'retentionD28',
  ],
};

const getMetricsChildren = () =>
  convertDataSourceAndSource(PC_AND_CONSOLE_DATA_SOURCE, {
    disableCondition: {
      gsd: 'additionalFields:dateType:daily||additionalFields:dateType:monthly',
      npd: 'additionalFields:dateType:daily||additionalFields:dateType:weekly',
      // famitsu: 'additionalFields:dateType:daily||additionalFields:dateType:monthly',
      noUpdate: 'additionalFields:dateType:daily||additionalFields:dateType:weekly',
      ampere: 'additionalFields:dateType:weekly',
    },
    disableMsgKey: {
      gsd: 'dbi_info_gsd_metrics_category_disabled_tips',
      npd: 'dbi_info_physical_monthly_only',
      // famitsu: 'dbi_info_console_japan_sales_metrics_category_disabled_tips',
      noUpdate: 'dbi_info_newzoo_bymonth',
      ampere: 'dbi_info_ampere_other_vendor_category_disabled_tips',
    },
  });

const PC_AND_CONSOLE_DETAIL_STORE_RANK_DOWNLOAD_SET: IChildProp[] = [
  {
    enumKey: 'dataSettings',
    enumTargetType: 'downloadMainCategory',
    type: 'checkboxGroup',
    children: [
      {
        isDefault: true,
        enumKey: 'steam',
        enumTargetType: 'platformGame',
      },
      {
        enumKey: 'epic',
        enumTargetType: 'platformGame',
      },
      {
        enumKey: 'playstationStoreRank',
        enumTargetType: 'platformPC',
      },
      {
        enumKey: 'xboxStoreRank',
        enumTargetType: 'platformPC',
      },
      {
        enumKey: 'nintendoStoreRank',
        enumTargetType: 'platformPC',
      },
    ],
  },
  {
    enumKey: 'basicInformation',
    enumTargetType: 'downloadMainCategory',
    type: 'checkboxGroup',
    children: [
      {
        enumKey: 'hasCompany',
        enumTargetType: 'downloadBasicInfo',
      },
      {
        enumKey: 'hasGenre',
        enumTargetType: 'downloadBasicInfo',
      },
      {
        enumKey: 'hasRelease',
        enumTargetType: 'downloadBasicInfo',
      },
      {
        enumKey: 'hasRegion',
        enumTargetType: 'downloadBasicInfo',
      },
      {
        enumKey: 'hasSteamId',
        enumTargetType: 'downloadBasicInfo',
      },
    ],
  },
  {
    enumKey: 'dataCalculation',
    enumTargetType: 'downloadMainCategory',
    type: 'subForm',
    className: 'form-item--top-18',
    children: [
      {
        enumKey: 'dateSettings',
        enumTargetType: 'downloadMainCategory',
        type: 'text',
        children: [
          {
            enumKey: 'isSeparateByTime',
            enumTargetType: 'downloadDataCalcu',
          },
        ],
      },
      {
        enumKey: 'regionSettings',
        enumTargetType: 'downloadMainCategory',
        type: 'radio',
        children: [
          {
            isDefault: true,
            enumKey: 'isCountryAggregated',
            enumTargetType: 'downloadDataCalcu',
          },
          {
            enumKey: 'isSeperateByCountries',
            enumTargetType: 'downloadDataCalcu',
          },
        ],
      },
      {
        enumKey: 'ratio',
        enumTargetType: 'downloadMainCategory',
        type: 'switch',
        isDefault: true,
      },
    ],
  },
  {
    enumKey: 'fileFormat',
    enumTargetType: 'downloadMainCategory',
    type: 'radio',
    children: [
      {
        isDefault: true,
        enumKey: 'exportExcel',
        enumTargetType: 'downloadOther',
      },
      {
        enumKey: 'exportCSV',
        enumTargetType: 'downloadOther',
      },
    ],
  },
];

const PC_AND_CONSOLE_DETAIL_KPI_TRENDS_DOWNLOAD_SET: IChildProp[] = [
  {
    enumKey: 'mutilMetrics',
    enumTargetType: 'downloadMainCategory',
    type: 'cascader',
    children: getMetricsChildren(),
  },
  {
    enumKey: 'basicInformation',
    enumTargetType: 'downloadMainCategory',
    type: 'checkboxGroup',
    children: [
      {
        enumKey: 'hasCompany',
        enumTargetType: 'downloadBasicInfo',
      },
      {
        enumKey: 'hasGenre',
        enumTargetType: 'downloadBasicInfo',
      },
      {
        enumKey: 'hasRelease',
        enumTargetType: 'downloadBasicInfo',
      },
      {
        enumKey: 'hasSteamId',
        enumTargetType: 'downloadBasicInfo',
      },
      {
        enumKey: 'hasSteamTags',
        enumTargetType: 'downloadBasicInfo',
      },
      {
        enumKey: 'hasGameMode',
        enumTargetType: 'downloadBasicInfo',
      },
      {
        enumKey: 'hasGameEngine',
        enumTargetType: 'downloadBasicInfo',
      },
    ],
  },
  {
    enumKey: 'dataCalculation',
    enumTargetType: 'downloadMainCategory',
    type: 'subForm',
    className: 'form-item--top-18',
    children: [
      {
        enumKey: 'timeSettings',
        enumTargetType: 'downloadMainCategory',
        type: 'radio',
        children: [
          {
            enumKey: 'isTimeAggregated',
            enumTargetType: 'downloadDataCalcu',
            defaultSelectCondition: 'additionalFields:tabType:countries',
          },
          {
            enumKey: 'isByTime',
            enumTargetType: 'downloadDataCalcu',
            defaultSelectCondition:
              'additionalFields:tabType:stats||additionalFields:tabType:kpiTrends',
          },
        ],
      },
      {
        enumKey: 'regionSettings',
        enumTargetType: 'downloadMainCategory',
        type: 'radio',
        children: [
          {
            enumKey: 'isCountryAggregated',
            enumTargetType: 'downloadDataCalcu',
            defaultSelectCondition:
              'additionalFields:tabType:stats||additionalFields:tabType:kpiTrends',
          },
          {
            enumKey: 'isByCountries',
            enumTargetType: 'downloadDataCalcu',
            defaultSelectCondition: 'additionalFields:tabType:countries',
          },
          {
            enumKey: 'isByRegion',
            enumTargetType: 'downloadDataCalcu',
          },
        ],
      },
      {
        label: 'Platform Settings',
        labelKey: 'dbi_label_platform_settings',
        value: 'deviceSettings',
        type: 'radio',
        children: [
          {
            isDefault: true,
            enumKey: 'isPlatformAggregated',
            enumTargetType: 'downloadDataCalcu',
          },
          {
            enumKey: 'isByPlatform',
            enumTargetType: 'downloadDataCalcu',
          },
        ],
      },
      {
        label: 'Segment Settings',
        labelKey: 'dbi_label_segment_settings_product',
        // region_method
        value: 'segmentSettings',
        type: 'radio',
        children: [
          {
            isDefault: true,
            enumKey: 'isSegmentAggregated',
            enumTargetType: 'downloadDataCalcu',
          },
          {
            enumKey: 'isBySegment',
            enumTargetType: 'downloadDataCalcu',
          },
        ],
      },
      {
        enumKey: 'ratio',
        enumTargetType: 'downloadMainCategory',
        type: 'switch',
        isDefault: true,
      },
    ],
  },
  {
    enumKey: 'fileFormat',
    enumTargetType: 'downloadMainCategory',
    type: 'radio',
    children: [
      {
        isDefault: true,
        enumKey: 'exportExcel',
        enumTargetType: 'downloadOther',
      },
      {
        enumKey: 'exportCSV',
        enumTargetType: 'downloadOther',
      },
    ],
  },
];

const PC_AND_CONSOLE_DETAIL_KPI_TRENDS_HOURLY_DOWNLOAD_SET: IChildProp[] = [
  {
    enumKey: 'singleMetrics',
    enumTargetType: 'downloadMainCategory',
    type: 'textGroup',
    children: [
      {
        isDefault: true,
        enumKey: 'source_steam__hourly_ccu',
        enumTargetType: 'metric',
      },
    ],
  },
  {
    enumKey: 'fileFormat',
    enumTargetType: 'downloadMainCategory',
    type: 'radio',
    children: [
      {
        isDefault: true,
        enumKey: 'exportExcel',
        enumTargetType: 'downloadOther',
      },
      {
        enumKey: 'exportCSV',
        enumTargetType: 'downloadOther',
      },
    ],
  },
];

const PC_AND_CONSOLE_DETAIL_STEAME_OVERLAP_DOWNLOAD_SET: IChildProp[] = [
  {
    enumKey: 'basicInformation',
    enumTargetType: 'downloadMainCategory',
    type: 'checkboxGroup',
    children: [
      {
        enumKey: 'hasCompany',
        enumTargetType: 'downloadBasicInfo',
      },
      {
        enumKey: 'hasGenre',
        isDefault: true,
        enumTargetType: 'downloadBasicInfo',
      },
      {
        enumKey: 'hasRelease',
        isDefault: true,
        enumTargetType: 'downloadBasicInfo',
      },
      {
        enumKey: 'hasSteamTags',
        isDefault: true,
        enumTargetType: 'downloadBasicInfo',
      },
    ],
  },
  {
    enumKey: 'dataCalculation',
    enumTargetType: 'downloadMainCategory',
    type: 'subForm',
    className: 'form-item--top-26',
    children: [
      {
        label: 'Country Settings',
        className: 'form-item--top-8',
        labelKey: 'dbi_label_country_settings',
        value: 'regionCountrySelectMultiple',
        type: 'regionCountrySelectMultiple',
      },
    ],
  },
  {
    enumKey: 'fileFormat',
    enumTargetType: 'downloadMainCategory',
    type: 'radio',
    children: [
      {
        isDefault: true,
        enumKey: 'exportExcel',
        enumTargetType: 'downloadOther',
      },
      {
        enumKey: 'exportCSV',
        enumTargetType: 'downloadOther',
      },
    ],
  },
];

// Other Vendor 使用独立的下载弹窗配置，避免与 Steam 的默认勾选项互相影响。
const PC_AND_CONSOLE_DETAIL_OTHER_VENDOR_OVERLAP_DOWNLOAD_SET: IChildProp[] = [
  {
    enumKey: 'basicInformation',
    enumTargetType: 'downloadMainCategory',
    type: 'checkboxGroup',
    children: [
      { enumKey: 'hasCompany', enumTargetType: 'downloadBasicInfo' },
      { enumKey: 'hasGenre', isDefault: true, enumTargetType: 'downloadBasicInfo' },
      { enumKey: 'hasRelease', isDefault: true, enumTargetType: 'downloadBasicInfo' },
      { enumKey: 'hasSteamTags', isDefault: true, enumTargetType: 'downloadBasicInfo' },
    ],
  },
  {
    labelKey: 'dbi_overlap_other_vendor_data_calculation_country',
    type: 'regionCountrySelectMultiple',
    value: 'regionCountrySelectMultiple',
    className: 'form-item--top-26',
  },
  {
    enumKey: 'fileFormat',
    enumTargetType: 'downloadMainCategory',
    type: 'radio',
    children: [
      { isDefault: true, enumKey: 'exportExcel', enumTargetType: 'downloadOther' },
      { enumKey: 'exportCSV', enumTargetType: 'downloadOther' },
    ],
  },
];

export default {
  pcAndConsoleDetailKpiTrends: PC_AND_CONSOLE_DETAIL_KPI_TRENDS_DOWNLOAD_SET,
  pcAndConsoleDetailKpiTrendsHourly: PC_AND_CONSOLE_DETAIL_KPI_TRENDS_HOURLY_DOWNLOAD_SET,
  pcAndConsoleDetailStoreRank: PC_AND_CONSOLE_DETAIL_STORE_RANK_DOWNLOAD_SET,
  pcAndConsoleDetailSteamOverlap: PC_AND_CONSOLE_DETAIL_STEAME_OVERLAP_DOWNLOAD_SET,
  pcAndConsoleDetailOtherVendorOverlap: PC_AND_CONSOLE_DETAIL_OTHER_VENDOR_OVERLAP_DOWNLOAD_SET,
};
